<template>
  <footer class="footer">
    <!-- Main Footer -->
    <div class="footer__main">
      <div class="container">
        <div class="footer__grid">
          <!-- Company Info -->
          <div class="footer__company">
            <router-link to="/" class="footer__logo">
              <div class="footer__logo-icon">
                <img src="/images/general/omega_logo.png" alt="Omega Metal Industries Logo" />
              </div>
              <span class="footer__logo-text">Omega Metal Industries</span>
            </router-link>
            <p class="footer__description">
              A leading industrial fabrication and machining company serving the oil & gas sector across Saudi Arabia and neighboring countries since 1996.
            </p>
            <div class="footer__certifications">
              <span class="footer__cert-badge">ISO 9001 Certified</span>
              <span class="footer__cert-badge">Est. 1996</span>
            </div>
          </div>

          <!-- Links -->
          <div class="footer__links">
            <h4 class="footer__title">Company</h4>
            <ul class="footer__list">
              <li><router-link to="/about">About Us</router-link></li>
              <li><router-link to="/qhse">QHSE Compliance</router-link></li>
              <li><router-link to="/contact">Contact Us</router-link></li>
            </ul>

            <h4 class="footer__title footer__title--spaced">Services</h4>
            <ul class="footer__list">
              <li><router-link to="/precision-machining">Precision Machining</router-link></li>
              <li><router-link to="/fabrication">Fabrication</router-link></li>
              <li><router-link to="/special-services">Special Services</router-link></li>
            </ul>
          </div>

          <!-- Contact Info -->
          <div class="footer__contact">
            <h4 class="footer__title">Contact Us</h4>
            <ul class="footer__contact-list">
              <li class="footer__contact-item">
                <svg class="footer__contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <span>{{ companyInfo.address.street }},<br>{{ companyInfo.address.city }} {{ companyInfo.address.zip }},<br>{{ companyInfo.address.country }}</span>
              </li>
              <li class="footer__contact-item">
                <svg class="footer__contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <a :href="`tel:${companyInfo.contact.phoneFormatted}`">{{ companyInfo.contact.phone }}</a>
              </li>
              <li class="footer__contact-item">
                <svg class="footer__contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>
                  <a :href="`mailto:${companyInfo.contact.emails.sales}`">{{ companyInfo.contact.emails.sales }}</a><br>
                  <a :href="`mailto:${companyInfo.contact.emails.info}`">{{ companyInfo.contact.emails.info }}</a>
                </span>
              </li>
              <li class="footer__contact-item">
                <svg class="footer__contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <span>
                  <span v-for="(time, i) in companyInfo.hours.formatted" :key="i">
                    {{ time.days }}: {{ time.hours }}<br v-if="i < companyInfo.hours.formatted.length - 1">
                  </span>
                </span>
              </li>
            </ul>

          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="footer__bottom">
      <div class="container">
        <div class="footer__bottom-content">
          <p class="footer__copyright">
            &copy; {{ currentYear }} Omega Metal Industries. All rights reserved.
          </p>
          <div class="footer__bottom-links">
            <router-link to="/privacy">Privacy Policy</router-link>
            <router-link to="/terms">Terms of Service</router-link>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { computed } from 'vue'
import { companyInfo } from '@/data/companyInfo'

export default {
  name: 'AppFooter',
  setup() {
    const currentYear = computed(() => new Date().getFullYear())

    return {
      currentYear,
      companyInfo
    }
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables' as *;
@use '@/assets/scss/mixins' as *;

.footer {
  background-color: $primary-900;
  color: $primary-200;

  &__main {
    padding: $spacing-12 0;

    @include lg {
      padding: $spacing-16 0;
    }
  }

  &__grid {
    display: grid;
    gap: $spacing-8;

    @include md {
      grid-template-columns: repeat(2, 1fr);
    }

    @include lg {
      grid-template-columns: 1.5fr 1fr 1.5fr;
    }
  }

  &__company {
    @include md {
      grid-column: span 2;
    }

    @include lg {
      grid-column: span 1;
    }
  }

  &__logo {
    display: inline-flex;
    align-items: center;
    gap: $spacing-3;
    margin-bottom: $spacing-4;
    text-decoration: none;
  }

  &__logo-icon {
    height: 48px;
    width: auto;

    img {
      height: 100%;
      width: auto;
      object-fit: contain;
    }
  }

  &__logo-text {
    font-size: $font-size-xl;
    font-weight: $font-weight-bold;
    color: $white;
  }

  &__description {
    margin-bottom: $spacing-4;
    font-size: $font-size-sm;
    line-height: $line-height-relaxed;
  }

  &__certifications {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-2;
  }

  &__cert-badge {
    padding: $spacing-1 $spacing-2;
    font-size: $font-size-xs;
    font-weight: $font-weight-medium;
    color: $white;
    background-color: rgba($white, 0.1);
    border-radius: $radius-sm;
  }

  &__title {
    margin-bottom: $spacing-4;
    font-size: $font-size-base;
    font-weight: $font-weight-semibold;
    color: $white;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    &--spaced {
      margin-top: $spacing-6;
    }
  }

  &__list {
    li {
      margin-bottom: $spacing-2;
    }

    a {
      font-size: $font-size-sm;
      color: $primary-300;
      text-decoration: none;
      transition: color $transition-fast;

      &:hover {
        color: $accent-400;
      }
    }
  }

  &__contact-list {
    margin-bottom: $spacing-4;
  }

  &__contact-item {
    display: flex;
    gap: $spacing-3;
    margin-bottom: $spacing-4;
    font-size: $font-size-sm;

    a {
      color: $primary-300;
      text-decoration: none;
      transition: color $transition-fast;

      &:hover {
        color: $accent-400;
      }
    }
  }

  &__contact-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: $accent-500;
  }

  &__bottom {
    padding: $spacing-4 0;
    border-top: 1px solid rgba($white, 0.1);
  }

  &__bottom-content {
    display: flex;
    flex-direction: column;
    gap: $spacing-4;
    text-align: center;

    @include md {
      flex-direction: row;
      justify-content: space-between;
      text-align: left;
    }
  }

  &__copyright {
    font-size: $font-size-sm;
    margin: 0;
  }

  &__bottom-links {
    display: flex;
    gap: $spacing-6;
    justify-content: center;

    a {
      font-size: $font-size-sm;
      color: $primary-300;
      text-decoration: none;
      transition: color $transition-fast;

      &:hover {
        color: $accent-400;
      }
    }
  }
}
</style>
